import { useMemo } from "react"
import { useOutletContext } from "react-router-dom"
import { useTranslation } from "react-i18next"

import type { DeviceDashboardOutletContext } from "types/dashboard.types"

import { getSpanTabPlotDefs, wStationAmbientTabPlotDefs } from "helpers/config/dashboards"
import useRoutes from "helpers/hooks/useRoutes"
import { getEntityHref } from "helpers/utils/entities"
import GeneralDashboard from "widgets/device/DeviceDashboard"
import { BodyContainer } from "widgets/styled/containers"
import DeviceTowerDashboard from "widgets/device/DeviceDashboard/DeviceTowerDashboard"
import NoticePanel from "widgets/common/NoticePanel"
import DeviceAlerts from "./DeviceAlerts"

function MainDashboard() {
  const { device } = useOutletContext<DeviceDashboardOutletContext>()
  const { t } = useTranslation()
  const { baseOrgURL } = useRoutes()

  const spanConfig = useMemo(
    () => ({
      type: "spanTab",
      plotDefs: getSpanTabPlotDefs(device.settings?.features ?? {}),
    }),
    [device.settings?.features],
  )

  const ambientConfig = useMemo(
    () => ({
      type: "wStationAmbientTab",
      plotDefs: wStationAmbientTabPlotDefs,
    }),
    [],
  )

  const Dashboard = useMemo(() => {
    // this condition is temporary until deviceType "tower"
    // then it can enter in the switch statement
    if (device.settings?.installation_type === "tower") {
      return <DeviceTowerDashboard device={device} />
    }

    switch (device.device_type) {
      case "weather_station":
        return <GeneralDashboard device={device} config={ambientConfig} />
      case "virtual_powerline":
        return <DeviceAlerts />
      default: {
        const spanId =
          device.settings?.installation_type === "powerline"
            ? device.settings.span_id
            : undefined

        return (
          <>
            {/* Temporary: drop it once operators stop looking for the span's
                ampacity here. */}
            {spanId ? (
              <NoticePanel
                body={t("spans.AMPACITY_MOVED")}
                action={{
                  label: t("geo_info.GO_TO_SPAN"),
                  href: getEntityHref("span", spanId, baseOrgURL),
                }}
              />
            ) : null}
            <GeneralDashboard device={device} config={spanConfig} />
          </>
        )
      }
    }
  }, [device, spanConfig, ambientConfig, t, baseOrgURL])

  return (
    <BodyContainer disableGutters maxWidth="xl">
      {Dashboard}
    </BodyContainer>
  )
}

export default MainDashboard
